import type { AbstractControl, AsyncValidator, AsyncValidatorFn, ValidationErrors } from '@angular/forms';
import { NG_ASYNC_VALIDATORS } from '@angular/forms';
import type { Observable } from 'rxjs';
import { of, timer } from 'rxjs';
import { Directive, Input, inject } from '@angular/core';
import { catchError, filter, map, switchMap } from 'rxjs/operators';
import { ESavePageErrorCodes } from '@common/enums/error-cores.enum';
import { ValidatorHttpService } from '@common/services/REST/validator.http.service';

type PageNameValidatorOptions = {
    siteId: string;
    checkIfDirty?: boolean;
};

export function CheckPageNameValidator(
    validatorHttpService: ValidatorHttpService,
    options: PageNameValidatorOptions
): AsyncValidatorFn {
    let initialValue = null;
    return (control: AbstractControl): Promise<ValidationErrors | null> | Observable<ValidationErrors | null> =>
        timer(500).pipe(
            filter(() => {
                if (!options.siteId) {
                    console.warn('PageNameValidatorDirective: siteId is not defined');
                    return false;
                }

                return true;
            }),
            switchMap(() => {
                if (!control.value) {
                    return of(null);
                }

                if (!initialValue) {
                    initialValue = control.value;
                }

                if (options.checkIfDirty && control.pristine && initialValue === control.value) {
                    return of(null);
                }

                return validatorHttpService.checkPageName(options.siteId, control.value);
            }),
            map(() => null),
            catchError(() => of({ [ESavePageErrorCodes.PAGE_NAME_ALREADY_USED]: true }))
        );
}

@Directive({
    selector: '[spPageNameCheck][formControl]',
    providers: [{ provide: NG_ASYNC_VALIDATORS, useExisting: PageNameValidatorDirective, multi: true }],
})
export class PageNameValidatorDirective implements AsyncValidator {
    private readonly validatorHttpService = inject(ValidatorHttpService);

    @Input() public spPageNameOptions: PageNameValidatorOptions = {
        siteId: null,
        checkIfDirty: false,
    };

    public validate(control: AbstractControl): Promise<ValidationErrors | null> | Observable<ValidationErrors | null> {
        return CheckPageNameValidator(this.validatorHttpService, this.spPageNameOptions)(control);
    }
}
